import { API_ERRO } from '@/constants/ConstantApiErro';
import { authStorage } from '@/lib/authStorage';
import { navigate } from '@/lib/navigation';
import ServiceAuthSSO from '@/services/ServiceAuthSSO';
import { notify } from '@/stores/useToast';
import axios from 'axios';
import { isEmpty } from 'lodash';

let controller = new AbortController();
let refreshPromise = null;

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API,
  headers: {
    Accepted: 'application/json',
  },
  signal: controller.signal,
});

function doesEndpointRequireToken(url) {
  const unprotectedEndpoints = ['app/version', 'auth/', 'api.ipify.org'];
  return !unprotectedEndpoints.some((endpoint) => url.includes(endpoint));
}

function hasTokenExpired() {
  const now = Math.floor(Date.now() / 1000);
  const exp = Number(authStorage.getItem('access_token_expiration_date'));
  return exp < now;
}

function getLoginRoute() {
  return ServiceAuthSSO.isSSO() ? '/login/sso' : '/login';
}

function handleInvalidToken() {
  authStorage.removeItem('access_Token');
  authStorage.removeItem('refresh_Token');
  authStorage.removeItem('access_token_expiration_date');

  notify.error('Sessão expirada. Faça login novamente para continuar.');

  if (ServiceAuthSSO.isSSO()) {
    ServiceAuthSSO.clearSSOData();
  }

  navigate(getLoginRoute());

  controller.abort(API_ERRO.TOKEN_EXPIRADO);
  resetAbortController();
}

function handleServerError() {
  controller.abort(API_ERRO.ERRO_SERVIDOR);
  resetAbortController();
}

function resetAbortController() {
  controller = new AbortController();
  axiosInstance.defaults.signal = controller.signal;
}

axiosInstance.interceptors.request.use(
  async (config) => {
    // DEV: bypass de auth p/ validar contra backend local (VITE_DISABLE_AUTH=true). NÃO usar em prod.
    if (import.meta.env.VITE_DISABLE_AUTH === 'true' && !import.meta.env.VITEST) {
      config.headers.Authorization = 'Bearer dev-local';
      config.headers.ipAddress = '127.0.0.1';
      return config;
    }

    const token = authStorage.getItem('access_Token');

    config.headers.ipAddress = localStorage.getItem('ipAddress');

    if (
      doesEndpointRequireToken(config.url) &&
      (isEmpty(token) || (!isEmpty(token) && hasTokenExpired()))
    ) {
      if (ServiceAuthSSO.isSSO() && !isEmpty(token)) {
        const refreshed = await trySilentRefresh();
        if (refreshed) {
          config.headers.Authorization = `Bearer ${refreshed}`;
          return config;
        }
      }
      handleInvalidToken();
    }

    if (token && !config.confirmingPassword) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isCancel(error)) return new Promise(() => {});

    const originalRequest = error.config;
    const failedRequestRoute = error.config?.url;

    if (
      error.response?.status === 401 &&
      ServiceAuthSSO.isSSO() &&
      !originalRequest._retry &&
      doesEndpointRequireToken(failedRequestRoute)
    ) {
      originalRequest._retry = true;

      const newToken = await trySilentRefresh();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }
      handleInvalidToken();
      return new Promise(() => {});
    }

    switch (error.response?.status) {
      case 401:
        if (
          !failedRequestRoute.includes('auth/oauth2/token') &&
          doesEndpointRequireToken(failedRequestRoute)
        ) {
          handleInvalidToken();
        }
        break;

      case 500: {
        const method = originalRequest?.method?.toUpperCase?.() ?? '';
        const detail =
          error.response?.data?.detail ?? error.response?.data?.message ?? error.message;
        console.error(
          `[API] Erro 500 | ${method} ${failedRequestRoute} | detail=${detail}`,
          error.response?.data ?? detail,
        );
        handleServerError();
        break;
      }

      default:
        break;
    }

    return Promise.reject(error);
  },
);

async function trySilentRefresh() {
  // ServiceAuthSSO.trySilentRefresh já persiste os tokens (persistSession, com exp
  // vindo do próprio JWT) e devolve o access_token (string) — só repassar.
  if (!refreshPromise) {
    refreshPromise = ServiceAuthSSO.trySilentRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export default axiosInstance;
